import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { completeTask } from "../../api/completeTask";
import { getProcessTasks } from "../../api/processRequest";
import { getTaskForm } from "../../api/tasks";
import FormRenderer, {
  FormRendererProps,
} from "../../pages/protected/components/FormRenderer";
import LoaderWithDescription from "../../components/LoaderWithDescription";
import extractInnerMessage, { extractApiScriptErrorMessage } from "../../utils/apiErrorUtils";
import { notify, notifyWithTitle } from "../../pages/public/components/Toast";

type Variables = Record<
  string,
  {
    type: string;
    value: string;
    valueInfo: object;
  }
>;

interface FormObject {
  form: [];
  variables: Variables;
}

const queryOpts = (requestId: string, taskId: string) => ({
  queryKey: ["task-form", requestId, taskId],
  queryFn: async () =>
    (await getTaskForm(requestId, taskId)) as unknown as FormObject,
  retry: false,
});

export const Route = createFileRoute("/__auth/task/$requestId/$taskId")({
  component: TaskWrapper,
  loader: async ({
    context: { queryClient },
    params: { requestId, taskId },
  }) => {
    const data = await queryClient.ensureQueryData(
      queryOpts(requestId, taskId)
    );
    return { ...data };
  },
});

function TaskWrapper() {
  const match = Route.useMatch();
  return <Tasks key={match.pathname} />;
}

function Tasks(): React.JSX.Element {
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const [isLoadingFinish, setIsLoadingFinish] = useState(false);

  const { data: formData, error } = useSuspenseQuery(
    queryOpts(params.requestId, params.taskId)
  );
  useEffect(() => {
    if (error) {
      navigate({ to: "/error" });
    }
  }, [error, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const callback: FormRendererProps["callback"] = async (
    data,
    requestId,
    taskId,
    reloadFormView
  ) => {
    try {
      await completeTask(data, requestId, taskId || "");
      const task = await getProcessTasks(requestId);
      let pathTask = "task";
  
      if (task.data[0]) {
        if (task.data[0].isSignatureTask) {
          pathTask = "signature";
        }
        void navigate({
          to: `/${pathTask}/${requestId}/${task.data[0].id}`,
        });
      } else {
        setIsLoadingFinish(true);
        await new Promise((resolve) => setTimeout(resolve, 1500));
        void navigate({ to: `/`, search: { processId: requestId } });
      }
    }catch(error){
      const apiErrorMessage = extractInnerMessage(error);

      console.error("error complete task:", apiErrorMessage);
      
      const scriptErrorMessage = extractApiScriptErrorMessage(apiErrorMessage);

      const isCustomScriptError = apiErrorMessage !== scriptErrorMessage;

      if(isCustomScriptError){
        notifyWithTitle(scriptErrorMessage, apiErrorMessage, "error");
      } else{
        notify(apiErrorMessage, "error", "", true);
      }

      reloadFormView && reloadFormView();
    }
  };

  return (
    <>
      {isLoadingFinish ? (
        <LoaderWithDescription
          title="Dados enviados com sucesso"
          subtitle="Você será direcionado para acompanhar sua solicitação"
        />
      ) : (
        <FormRenderer
          callback={callback}
          cancel={() => {
            void navigate({
              to: `/`,
              search: { processId: params.requestId } as any,
            });
          }}
          formData={formData}
          processId={params.requestId}
          taskId={params.taskId}
          type="task"
        />
      )}
    </>
  );
}
